import Phaser from "phaser";
import type { Controls } from "./Controls";
import type { AttackKind } from "../config";

export interface KeyboardKeyMap {
  LEFT: string; RIGHT: string; UP: string; DOWN: string;
  // 6-кнопочная схема (если задана)
  LP?: string; HP?: string; LK?: string; HK?: string;
  RUN?: string;
  // legacy aliases
  PUNCH?: string; KICK?: string; BLOCK: string;
  START?: string;
}

export class KeyboardControls implements Controls {
  readonly source: string = "KEYBOARD";

  private keys: Record<string, Phaser.Input.Keyboard.Key | undefined> = {};
  private pendingKind: AttackKind | null = null;
  private prevDown: Record<string, boolean> = {};

  // throw-окно: одновременное LP+LK
  private lpAt = -1; private lkAt = -1;
  private throwWindowMs = 80;
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene, keyMap: KeyboardKeyMap, label?: string) {
    this.scene = scene;
    const KB = scene.input.keyboard!;
    const k = (s?: string) => s ? KB.addKey(s) : undefined;
    this.keys = {
      left: k(keyMap.LEFT), right: k(keyMap.RIGHT),
      up: k(keyMap.UP), down: k(keyMap.DOWN),
      lp: k(keyMap.LP || keyMap.PUNCH),
      hp: k(keyMap.HP),
      lk: k(keyMap.LK || keyMap.KICK),
      hk: k(keyMap.HK),
      block: k(keyMap.BLOCK),
      run: k(keyMap.RUN),
      start: k(keyMap.START),
      // legacy
      punch: k(keyMap.PUNCH || keyMap.LP),
      kick: k(keyMap.KICK || keyMap.LK),
    };
    if (label) (this as any).source = label;
  }

  update(_time: number): void {
    const now = this.scene.time.now;
    // Edge-trigger для атак — определяем kind на момент нажатия.
    for (const key of ["lp", "hp", "lk", "hk"] as const) {
      const k = this.keys[key];
      if (!k) continue;
      const isDown = k.isDown;
      const wasDown = !!this.prevDown[key];
      if (isDown && !wasDown) this.onAttackDown(key, now);
      this.prevDown[key] = isDown;
    }
  }

  private onAttackDown(key: "lp" | "hp" | "lk" | "hk", now: number) {
    if (key === "lp") {
      this.lpAt = now;
      if (now - this.lkAt <= this.throwWindowMs) { this.pendingKind = "throw"; return; }
    } else if (key === "lk") {
      this.lkAt = now;
      if (now - this.lpAt <= this.throwWindowMs) { this.pendingKind = "throw"; return; }
    }
    const up = this.isUp(), down = this.isDown(), fwd = this.isRight();
    let kind: AttackKind;
    if (key === "lp") kind = fwd ? "straight" : "jab";
    else if (key === "hp") kind = down ? "uppercut" : "hp";
    else if (key === "lk") kind = up ? "jump_kick" : "lk";
    else kind = down ? "sweep" : "kick";
    this.pendingKind = kind;
  }

  isLeft()  { return !!this.keys.left?.isDown; }
  isRight() { return !!this.keys.right?.isDown; }
  isUp()    { return !!this.keys.up?.isDown; }
  isDown()  { return !!this.keys.down?.isDown; }
  isPunch() { return !!this.keys.lp?.isDown || !!this.keys.hp?.isDown; }
  isKick()  { return !!this.keys.lk?.isDown || !!this.keys.hk?.isDown; }
  isBlock() { return !!this.keys.block?.isDown; }
  isLP() { return !!this.keys.lp?.isDown; }
  isHP() { return !!this.keys.hp?.isDown; }
  isLK() { return !!this.keys.lk?.isDown; }
  isHK() { return !!this.keys.hk?.isDown; }
  isRun() { return !!this.keys.run?.isDown; }

  attackKind(): AttackKind | null {
    if (!this.pendingKind) return null;
    const k = this.pendingKind;
    this.pendingKind = null;
    return k;
  }

  justPressed(action: string): boolean {
    const k = (this.keys as any)[action] as Phaser.Input.Keyboard.Key | undefined;
    if (!k) return false;
    return Phaser.Input.Keyboard.JustDown(k);
  }
}
